// ============================================================================
// EndOfDaySystem.js - COMPLETE END OF DAY + NIGHTLY NEGOTIATION SYSTEM
// ============================================================================

import { debug } from '../lib/debug.js';
import { calculateTier } from './ReputationEngine.js';
import { RELATIONS_THRESHOLDS } from './RelationsEngine.js';
import { recoverAllRoutes } from './RouteDemandSystem.js';
import { ANOMALOUS_BEHAVIORS } from './AnomalousBehaviors.js';

// ============================================================================
// TIERED THRESHOLDS BY AIRPORT PRESTIGE
// ============================================================================
// Regional/Medium airports (Tier 1-2): Easier to unlock
// Major hubs (Tier 3): Moderate difficulty
// Super/Mega hubs (Tier 4-5): Hardest to unlock
// ============================================================================
export const NEGOTIATION_THRESHOLDS_BY_TIER = {
  1: 10,   // Regional airports (KRDM, KEUG, etc.) - ~20 flights
  2: 15,   // Medium airports (KSLC, KLAS, etc.) - ~30 flights
  3: 25,   // Major hubs (KSFO, KLAX, etc.) - ~50 flights
  4: 35,   // Super hubs (KORD, KJFK, etc.) - ~70 flights
  5: 45,   // Mega international (EGLL, RJTT, etc.) - ~90 flights
};

// Get threshold for a specific airport tier
function getThresholdForTier(tier) {
  return NEGOTIATION_THRESHOLDS_BY_TIER[tier] || 10;
}

export const AIRPORTS_BY_DISTANCE_FROM_PDX = [
  // Tier 1
  { code: 'KRDM', name: 'Redmond Municipal', distance: 117, tier: 1 },
  { code: 'KSEA', name: 'Seattle-Tacoma', distance: 129, tier: 1 },
  { code: 'KEUG', name: 'Eugene', distance: 110, tier: 1 },
  { code: 'KBOI', name: 'Boise', distance: 344, tier: 1 },
  { code: 'KSMF', name: 'Sacramento', distance: 479, tier: 1 },

  // Tier 2
  { code: 'KSLC', name: 'Salt Lake City', distance: 546, tier: 2 },
  { code: 'KOAK', name: 'Oakland', distance: 531, tier: 2 },
  { code: 'KSJC', name: 'San Jose', distance: 569, tier: 2 },
  { code: 'KLAS', name: 'Las Vegas', distance: 760, tier: 2 },
  { code: 'KPHX', name: 'Phoenix', distance: 1009, tier: 2 },
  { code: 'KDEN', name: 'Denver', distance: 991, tier: 2 },
  { code: 'KABQ', name: 'Albuquerque', distance: 1159, tier: 2 },

  // Tier 3
  { code: 'KSFO', name: 'San Francisco', distance: 550, tier: 3 },
  { code: 'KLAX', name: 'Los Angeles', distance: 834, tier: 3 },
  { code: 'KSAN', name: 'San Diego', distance: 933, tier: 3 },

  // Tier 4
  { code: 'KORD', name: "Chicago O'Hare", distance: 1739, tier: 4 },
  { code: 'KDFW', name: 'Dallas-Fort Worth', distance: 1616, tier: 4 },
  { code: 'KATL', name: 'Atlanta', distance: 2172, tier: 4 },
  { code: 'KJFK', name: 'New York JFK', distance: 2454, tier: 4 },
  { code: 'KMIA', name: 'Miami', distance: 2703, tier: 4 },

  // Tier 5
  { code: 'EGLL', name: 'London Heathrow', distance: 4800, tier: 5 },
  { code: 'LFPG', name: 'Paris CDG', distance: 5100, tier: 5 },
  { code: 'RJTT', name: 'Tokyo Haneda', distance: 4900, tier: 5 },
  { code: 'VHHH', name: 'Hong Kong', distance: 6500, tier: 5 },
  { code: 'WSSS', name: 'Singapore Changi', distance: 8500, tier: 5 },
];

export function createEndOfDayState() {
  return {
    showEndOfDayModal: false,
    showNegotiationModal: false,
    endOfDayTriggered: false,
    currentDayNumber: 1,
    lastNegotiationDay: 0,
    negotiationAirport: null,
    lastLawFirm: null,
    playerSlots: {
      KPDX: { slots: Infinity, acquired: 'hub' },
      KSEA: { slots: 3, acquired: 'starting' },
      KBOI: { slots: 3, acquired: 'starting' },
      KRDM: { slots: 3, acquired: 'starting' },
    },
  };
}

export function shouldEndDay(worldState) {
  const gameTime = new Date(worldState.gameTime);
  const gameHour = gameTime.getUTCHours();

  if (worldState.endOfDay?.endOfDayTriggered) {
    return false;
  }

  if (gameHour >= 22) {
    return true;
  }

  return false;
}

export function canManuallyEndDay(worldState) {
  const flights = worldState?.flights || [];

  const hasActiveFlights = flights.some(f =>
    f.phase !== 'completed' &&
    f.phase !== 'cancelled' &&
    f.phase !== 'scheduled'
  );

  const gameTime = new Date(worldState?.gameTime || Date.now());
  const todayStr = gameTime.toISOString().slice(0, 10);

  const hasScheduledToday = flights.some(f => {
    if (f.phase !== 'scheduled') return false;
    const depTime = new Date(f.scheduledDeparture);
    return depTime.toISOString().slice(0, 10) === todayStr;
  });

  return !hasActiveFlights && !hasScheduledToday;
}

export function getNextNegotiationAirport(worldState) {
  const mainSlots = worldState?.slots || {};
  const playerRelations = worldState?.relations?.playerRelations?.airports || {};
  const completedNegotiations = worldState?.completedNegotiations || [];

  debug('[EndOfDay] Checking relations for negotiation trigger...');
  debug('[EndOfDay] Current slots:', Object.keys(mainSlots));
  debug('[EndOfDay] Player relations:', Object.keys(playerRelations).map(k => `${k}:${playerRelations[k]?.current || 0}`));

  // Find all airports that have crossed their tier-specific relations threshold
  const eligibleAirports = [];

  for (const [airportCode, relData] of Object.entries(playerRelations)) {
    const currentRelations = relData?.current || 0;

    // Find airport info from our list (for tier and name)
    const airportInfo = AIRPORTS_BY_DISTANCE_FROM_PDX.find(a => a.code === airportCode);
    const airportTier = airportInfo?.tier || 1;
    const requiredThreshold = getThresholdForTier(airportTier);

    // Skip if relations below tier-specific threshold
    if (currentRelations < requiredThreshold) {
      continue;
    }

    // Skip if already have slots at this airport
    const hasSlots = mainSlots[airportCode] && mainSlots[airportCode].length > 0;
    if (hasSlots) {
      debug('[EndOfDay] Skipping', airportCode, '- already have slots');
      continue;
    }

    // Skip if already completed negotiation for this airport
    if (completedNegotiations.includes(airportCode)) {
      debug('[EndOfDay] Skipping', airportCode, '- already negotiated');
      continue;
    }

    eligibleAirports.push({
      code: airportCode,
      name: airportInfo?.name || airportCode,
      distance: airportInfo?.distance || 0,
      tier: airportTier,
      relations: currentRelations,
      threshold: requiredThreshold
    });

    debug('[EndOfDay] Eligible:', airportCode, 'relations:', currentRelations, '(tier', airportTier, 'threshold:', requiredThreshold + ')');
  }

  if (eligibleAirports.length === 0) {
    debug('[EndOfDay] No airports above their tier-specific threshold');
    return null;
  }

  // Return the airport with the HIGHEST relations (relative to threshold is already passed)
  eligibleAirports.sort((a, b) => b.relations - a.relations);
  const bestAirport = eligibleAirports[0];

  debug('[EndOfDay] Next negotiation:', bestAirport.code, 'with relations:', bestAirport.relations, '(tier', bestAirport.tier + ')');
  return bestAirport;
}

export function triggerEndOfDay(worldState) {
  const dayNumber = worldState.dayNumber || 1;
  const nextAirport = getNextNegotiationAirport(worldState);

  return {
    ...worldState,
    isPaused: true,
    endOfDay: {
      ...(worldState.endOfDay || createEndOfDayState()),
      showEndOfDayModal: true,
      endOfDayTriggered: true,
      currentDayNumber: dayNumber,
      negotiationAirport: nextAirport,
    },
  };
}

export function proceedToNegotiation(worldState) {
  const endOfDay = worldState.endOfDay || createEndOfDayState();

  if (!endOfDay.negotiationAirport) {
    return advanceToNextDay(worldState);
  }

  return {
    ...worldState,
    endOfDay: {
      ...endOfDay,
      showEndOfDayModal: false,
      showNegotiationModal: true,
      endOfDayTriggered: false,
    },
  };
}

export function completeNegotiation(worldState, result) {
  const endOfDay = worldState.endOfDay || createEndOfDayState();
  const airport = endOfDay.negotiationAirport;

  let newPlayerSlots = { ...endOfDay.playerSlots };
  let newCash = worldState.airline?.cash || 0;

  if (result.won && result.slotsWon > 0) {
    newPlayerSlots[airport.code] = {
      slots: result.slotsWon,
      acquired: new Date(worldState.gameTime).toISOString(),
      bidAmount: result.totalPaid,
    };
    newCash -= result.totalPaid;
  }

  return {
    ...worldState,
    airline: {
      ...worldState.airline,
      cash: newCash,
    },
    endOfDay: {
      ...endOfDay,
      showNegotiationModal: false,
      playerSlots: newPlayerSlots,
      lastNegotiationDay: endOfDay.currentDayNumber,
      lastLawFirm: result.lawFirm || endOfDay.lastLawFirm,
    },
  };
}

export function skipNegotiation(worldState) {
  const endOfDay = worldState.endOfDay || createEndOfDayState();

  return {
    ...worldState,
    endOfDay: {
      ...endOfDay,
      showNegotiationModal: false,
      lastNegotiationDay: endOfDay.currentDayNumber,
    },
  };
}

export function advanceToNextDay(worldState) {
  const currentTime = new Date(worldState.gameTime);

  const nextDay = new Date(currentTime);
  nextDay.setUTCDate(nextDay.getUTCDate() + 1);
  nextDay.setUTCHours(6, 0, 0, 0);

  const newDayNumber = (worldState.dayNumber || 1) + 1;

  // ROUTE DEMAND: Recover all routes on day advance (respects abandoned route cooldowns)
  const updatedRouteDemand = { ...(worldState.routeDemand || {}) };
  const abandonedRoutes = { ...(worldState.abandonedRoutes || {}) };
  recoverAllRoutes(updatedRouteDemand, abandonedRoutes, newDayNumber);

  // REVENUE ENGINE: Generate passive income if player owns one
  let revenueEngineIncome = 0;
  const fleet = worldState.fleet || [];
  const hasRevenueEngine = fleet.some(ac =>
    ac.eosId === 'revenue-engine' || ac.aircraftId === 'revenue-engine' || ac.id === 'revenue-engine'
  );
  if (hasRevenueEngine) {
    const behaviorDef = ANOMALOUS_BEHAVIORS['revenue-engine'];
    revenueEngineIncome = behaviorDef?.config?.passiveIncomeCentsPerDay || 1000000000; // $10M default
    debug('[EndOfDay] Revenue Engine generated:', revenueEngineIncome / 100, 'dollars');
  }

  // Calculate new cash with Revenue Engine income
  const currentCash = worldState.airline?.cash || 0;
  const newCash = currentCash + revenueEngineIncome;

  return {
    ...worldState,
    gameTime: nextDay.toISOString(),
    dayNumber: newDayNumber,
    isPaused: false,
    routeDemand: updatedRouteDemand,
    abandonedRoutes, // Update with cleaned-up cooldowns
    airline: {
      ...worldState.airline,
      cash: newCash,
    },
    endOfDay: {
      ...(worldState.endOfDay || createEndOfDayState()),
      showEndOfDayModal: false,
      showNegotiationModal: false,
      endOfDayTriggered: false,
      currentDayNumber: newDayNumber,
      negotiationAirport: null,
    },
    stats: {
      ...worldState.stats,
      dailyFlights: 0,
      dailyRevenue: 0,
      dailyDelays: 0,
      dailyCancellations: 0,
    },
    // Track Revenue Engine income for UI notification
    revenueEngineIncomeToday: revenueEngineIncome > 0 ? revenueEngineIncome : undefined,
  };
}

export function generateSealedAIBids(airport, slots, competitors) {
  const bids = [];

  const baseBidByTier = {
    1: 50000,
    2: 100000,
    3: 200000,
    4: 400000,
    5: 800000,
  };

  const baseBid = baseBidByTier[airport.tier] || 50000;

  for (const slot of slots) {
    for (const airline of competitors) {
      if (Math.random() > 0.7) continue;

      const aggression = 0.8 + Math.random() * 0.5;
      const variance = 0.8 + Math.random() * 0.4;
      const amount = Math.round((baseBid * aggression * variance) / 5000) * 5000;

      bids.push({
        slotId: slot.id,
        airline,
        amount: Math.max(45000, amount),
      });
    }
  }

  return bids;
}

export const EXPECTED_BID_RANGES = {
  1: { min: 40000, max: 75000, typical: 55000 },
  2: { min: 80000, max: 160000, typical: 110000 },
  3: { min: 150000, max: 350000, typical: 220000 },
  4: { min: 300000, max: 700000, typical: 450000 },
  5: { min: 600000, max: 1500000, typical: 900000 },
};
